import { useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from './redux';
import {
  fetchLookupData,
  selectLookupData,
  selectLookupLoading,
  selectLookupError,
  selectIsCacheValid,
  selectHasCacheData,
  selectIsVersionStale,
  clearCache,
  clearAllCache,
  clearExpiredCache,
  SYSTEM_LOOKUP_CACHE_PREFIX
} from '@/store/slices/lookupSlice';
// 刻意走 `@/api` barrel 而不是深 import `@/api/api`：深 import 能再砍掉
// resourceMapApi(含 13.6 KB mock 数据)/taskKindApi/schedulerApi 等无关模块，
// 但全仓有 37 个测试文件用 `vi.mock('@/api')` 覆盖这些 hook 的取数——换路径后
// 那些 mock 会**静默失效**(曾经只有 useRegionLookup.test.ts 断言够硬会报错，其余 36 个
// 悄悄拿到真实的空数据)。这点 transform 时间不值得换一个无声的失败模式。
import { decorateGroupLookup, listingGroups } from '@/features/groups/lookup';
import { DEFAULT_LISTING_KINDS, LISTING_KINDS } from '@/config/listing-kind';
import { useAllowedListingKinds } from '@/hooks/useAllowedListingKinds';
import type { ListingKind } from '@/types/listing';
import {groupsApi, lookupApi, lookupTableApi, listingsApi, tagsApi, userApi, deptApi, catalogApi, ownersApi, taskTemplatesApi} from "@/api";

// Hook返回类型
interface UseLookupResult {
  data: any[] | null;
  loading: boolean;
  error: string | null;
}

// 防止重复请求的全局跟踪
const pendingRequests = new Set<string>();

export const useLookup = (key: string, apiCall: () => Promise<any>, options?: { enabled?: boolean }): UseLookupResult => {
  const enabled = options?.enabled !== false;
  const dispatch = useAppDispatch();

  // 使用 useRef 来存储 apiCall，避免依赖变化
  const apiCallRef = useRef(apiCall);
  apiCallRef.current = apiCall;

  // 使用 useRef 来追踪是否已经检查过（组件生命周期内只检查一次）
  const hasCheckedRef = useRef(false);
  // 存储首次挂载时的缓存有效性状态
  const initialCacheValidRef = useRef<boolean | null>(null);

  // 从 Redux store 获取数据
  const data = useAppSelector(state => selectLookupData(state, key));
  const loading = useAppSelector(state => selectLookupLoading(state, key));
  const error = useAppSelector(state => selectLookupError(state, key));
  const isCacheValid = useAppSelector(state => selectIsCacheValid(state, key));
  const hasCacheData = useAppSelector(state => selectHasCacheData(state, key));
  const versionStale = useAppSelector(state => selectIsVersionStale(state, key));

  // 获取数据的函数 - 使用稳定的依赖
  const fetchData = useCallback(async () => {
    // 防止重复请求（全局级别）
    if (pendingRequests.has(key)) {
      return;
    }

    pendingRequests.add(key);

    try {
      await dispatch(fetchLookupData({ key, apiCall: apiCallRef.current }));
    } finally {
      pendingRequests.delete(key);
    }
  }, [key, dispatch]);

  // 当 key 变化时重置检查标记。必须排在下面的检查之前：这一轮就要按新 key 取数
  useEffect(() => {
    hasCheckedRef.current = false;
    initialCacheValidRef.current = null;
  }, [key]);

  // 初始化时检查是否需要获取数据
  // 策略：只在组件首次挂载时检查，之后不再因为缓存过期而重新请求
  // 这样可以防止flyout中的表单因为lookup过期重载而被清空
  useEffect(() => {
    // disabled 时完全不发请求,等调用方把 enabled 切到 true(如 filter dropdown
    // 第一次打开)再触发。允许 hasCheckedRef 保持 false,这样 enabled 切 true 时
    // 此 effect 会重新进入并 fetchData。
    if (!enabled) {
      return;
    }

    // 组件已经检查过，不再重复检查
    if (hasCheckedRef.current) {
      return;
    }

    // 如果正在加载中，等待加载完成
    if (loading) {
      return;
    }

    // 如果已经有错误，不重试
    if (error) {
      hasCheckedRef.current = true;
      return;
    }

    // 记录首次检查时的缓存有效性
    if (initialCacheValidRef.current === null) {
      initialCacheValidRef.current = isCacheValid;
    }

    // 只在首次挂载时检查：没有缓存数据 或 首次检查时缓存已过期 → 请求
    // 注意：如果已经有数据但缓存过期，不会触发重新请求（惰性检查）
    if (!hasCacheData || (initialCacheValidRef.current === false && !hasCacheData)) {
      hasCheckedRef.current = true;
      void fetchData();
    } else {
      // 有有效数据，标记为已检查
      hasCheckedRef.current = true;
    }
  }, [key, hasCacheData, loading, error, fetchData, enabled]);
  // 注意：故意不将 isCacheValid 放入依赖数组，防止缓存过期时触发重新请求

  // 后端配置版本变了（GET /system/config/versions → applyServerVersions 标了 versionStale）：
  // 数据真的改了（例：表单改名），已挂载和新挂载的组件都在后台重拉一次，重拉期间照旧显示旧数据。
  // TTL 过期仍是惰性的（见上），不受影响。每轮过期只试一次，失败不连环重试。
  const staleFetchTriedRef = useRef(false);
  useEffect(() => {
    if (!versionStale) {
      staleFetchTriedRef.current = false;
      return;
    }
    if (!enabled || loading || staleFetchTriedRef.current) return;
    staleFetchTriedRef.current = true;
    void fetchData();
  }, [versionStale, enabled, loading, fetchData]);

  return {
    data,
    loading,
    error
  };
};

/**
 * 只回在用的地点。默认值在后端（main-api `listing_filters.py`），前端不传含停用。
 * 要停用的只有 /listings 管理页（config/listing-filters.ts），它不走这里。
 */
export interface ListingLookupOptions {
  enabled?: boolean
  /** 默认 ['RENTAL']。内部按权限裁剪，见 useAllowedListingKinds */
  kind?: readonly ListingKind[]
}

/**
 * 缓存键。['RENTAL'] 不带后缀，存量键不变。其余按 LISTING_KINDS 的顺序加后缀，
 * 书写顺序不影响键。新组合要在 lookupSlice 的 RESOURCE_TO_CACHE_KEYS 登记，否则版本号失效时清不到。
 */
export const listingLookupKey = (base: string, kinds: readonly ListingKind[]): string => {
  const isDefault = kinds.length === DEFAULT_LISTING_KINDS.length && kinds.every((kind) => DEFAULT_LISTING_KINDS.includes(kind));
  if (isDefault) return base;
  return `${base}_${LISTING_KINDS.filter((kind) => kinds.includes(kind)).join('_').toLowerCase()}`;
};

const EMPTY_LOOKUP: UseLookupResult = { data: [], loading: false, error: null };

// 默认值由后端给（在用的 RENTAL，规则见 main-api `listing_filters.py`）。前端只写 kind。
// 裁剪后没有 kind 时不发请求：`kind: []` 后端回 422。
const useKindedListingLookup = (base: string, nameFields: string, opts?: ListingLookupOptions): UseLookupResult => {
  const kinds = useAllowedListingKinds(opts?.kind ?? DEFAULT_LISTING_KINDS);
  const hasKinds = kinds.length > 0;
  const result = useLookup(
    listingLookupKey(base, kinds),
    () => listingsApi.getListingLookup({
      nameFields,
      orderBy: 'nickname',
      kind: kinds,
    }),
    { enabled: hasKinds && opts?.enabled !== false }
  );
  return hasKinds ? result : EMPTY_LOOKUP;
};

// 带经纬度，Route Map 要用。key: "listings"。
export const useListingLookup = (opts?: ListingLookupOptions): UseLookupResult =>
  useKindedListingLookup('listings', 'id nickname latitude longitude', opts);

// 带 groupId、listingKind。key: "listings_with_kind_group"。
// 键名带 kind：换字段要换键，否则浏览器里的旧缓存没有 listingKind，办公室会排到 Rentals tab。
export const useListingWithGroupLookup = (opts?: ListingLookupOptions): UseLookupResult =>
  useKindedListingLookup('listings_with_kind_group', 'id nickname groupId listingKind', opts);

// 用于getDepartmentsLookup (key: "departments")
export const useDepartmentLookup = (opts?: { enabled?: boolean }): UseLookupResult => {
  return useLookup('departments', () => deptApi.getDeptLookup({nameFields: 'id name labelI18n parentId', orderBy: 'name'}), { enabled: opts?.enabled });
};

// 用于getTagLookup (key: "tags")
// 'tags' lookup 的 key 与取数在一处定义，供 useTagLookup 与 useRefreshTagLookup 共用（DRY）。
// nameFields 带上 groupName/color/icon —— filter 只用 id/name（多带无害），但 tag picker
// 需要这些字段渲染彩色 chip + 分组，让 picker 与 filter 共用这一份 Redux 缓存（单一真相源）。
export const TAG_LOOKUP_KEY = 'tags';
const tagLookupApiCall = () =>
  tagsApi.getTagLookup({ nameFields: 'id name entityTypeCode groupName color icon', orderBy: 'name' });

export const useTagLookup = (opts?: { enabled?: boolean }): UseLookupResult => {
  return useLookup(TAG_LOOKUP_KEY, tagLookupApiCall, { enabled: opts?.enabled });
};

/**
 * 新建/改名/删除 tag 后调用：重新拉取 'tags' lookup 并把结果推回 Redux。
 *
 * 为什么不是 clearCache：`useLookup` 一旦在某次挂载里检查过缓存（hasCheckedRef）
 * 就不再自动重拉（有意设计，防 flyout 表单因 lookup 过期重载被清空）。所以单纯
 * clearCache 删掉数据，已挂载的列表 filter 不会重新请求。直接 dispatch
 * fetchLookupData 把最新 tag 列表写进 store，`selectLookupData` 的订阅者
 * （所有 useTagLookup 消费者，如各列表 tag filter）随即重渲染拿到新选项——无需整页刷新。
 */
export const useRefreshTagLookup = (): (() => void) => {
  const dispatch = useAppDispatch();
  return useCallback(() => {
    void dispatch(fetchLookupData({ key: TAG_LOOKUP_KEY, apiCall: tagLookupApiCall }));
  }, [dispatch]);
};

// 用于getLookup，使用传入的字典名称作为key。
// cache key 加 SYSTEM_LOOKUP_CACHE_PREFIX 前缀：所有 lookup-table 字典共用后端单一
// `lookup_tables` 版本号（bundle），applyServerVersions 按此前缀整批失效（见 lookupSlice）。
// includeExtra=true 时改走 GET /system/lookup-table/tables/{code}?include_extra=true，
// 额外拿到 labelI18n / iconUrl / sortOrder / description（下拉要按当前语言渲染标签时用，
// 配合 pickI18n）。两种形态**必须分开缓存 key**：同一个 key 下混存会让先到的那份
// 决定后来者有没有 labelI18n。前缀仍是 SYSTEM_LOOKUP_CACHE_PREFIX，
// 所以 lookup_tables 版本号 bump 时两份一起失效。
// includeDisabled=true 连已停用项一起取（?include_disabled=true）：只用于把历史数据里的停用 code
// 显示成正式标签（字典停用 = 以后不再选，不是历史记录作废）；下拉选项一律不带。同样单独缓存 key。
export const useSystemLookup = (
  dictionaryName: string,
  opts?: { enabled?: boolean; includeExtra?: boolean; includeDisabled?: boolean }
): UseLookupResult => {
  const includeExtra = opts?.includeExtra === true;
  const includeDisabled = opts?.includeDisabled === true;
  return useLookup(
    `${SYSTEM_LOOKUP_CACHE_PREFIX}${dictionaryName}${includeExtra ? ':extra' : ''}${includeDisabled ? ':all' : ''}`,
    () => (includeExtra
      ? lookupTableApi.getLookupTable(dictionaryName, true, includeDisabled)
      : lookupApi.getLookup(dictionaryName, { includeDisabled })),
    { enabled: opts?.enabled }
  );
};

// 租户级 catalog 下拉（skill / position / service_line）—— Option B 的统一 facade。
// 与 useSystemLookup（全局 lookup-table）分开：catalog 是租户级实体，走各自 POST /lookup 端点、
// 按当前租户隔离；返回 [{id, code, label, ...}]，组件按 .code / .label 消费（与旧 lookup 兼容）。
// 各 kind 独立缓存（key = catalog_<kind>）。
export const useCatalog = (
  kind: 'skill' | 'position' | 'service_line',
  opts?: { enabled?: boolean }
): UseLookupResult => {
  const apiCall =
    kind === 'skill'
      ? () => catalogApi.lookupSkills()
      : kind === 'position'
        ? () => catalogApi.lookupPositions()
        : () => catalogApi.lookupServiceLines();
  return useLookup(`catalog_${kind}`, apiCall, { enabled: opts?.enabled });
};

// 分组下拉（key: "groups_v2"）。行带 label「WIR · Windsor Island」、pathLabel「Zone 1 › WIR · Windsor Island」、depth，
// 按树的顺序排（见 features/groups/lookup.ts）。下拉显示 pathLabel；`name` 保持后端原值。
// key 带 v2：旧 "groups" 缓存（localStorage）只有 id/name，没有层级字段，换 key 让它自然失效。
// 后端 lookup 默认只回在用的分组。默认只给能挂房源的分组（resort）：现在各处都按房源直接挂的分组精确匹配，
// 选 Zone 选不到房源；后端阶段 3b 改成含下级后，用 includeContainers 逐个放开。
// 只做 id → 名字解析（不给人选）的地方传 includeContainers: true。
const fetchGroupLookup = () =>
  groupsApi.getGroupLookup({ nameFields: 'id code name labelI18n parentId pathIds typeCode entityTypeCode isActive', orderBy: 'code' });

export const useGroupLookup = (opts?: { enabled?: boolean; includeContainers?: boolean }): UseLookupResult => {
  const res = useLookup('groups_v2', fetchGroupLookup, { enabled: opts?.enabled });
  const all = opts?.includeContainers === true;
  // 名字按界面语言取 labelI18n（不 import useLocale：useLookup 的 import 图有阈值，见 apiBarrelGraph.test）
  const { i18n } = useTranslation();
  const locale = (i18n.language ?? 'en').split('-')[0];
  const data = useMemo(() => {
    if (!res.data) return res.data;
    const rows = decorateGroupLookup(res.data, locale);
    return all ? rows : listingGroups(rows);
  }, [res.data, all, locale]);
  return { ...res, data };
};

// 用于owner下拉选择 (key: "owners_v2")
// key 带 v2：旧 "owners" 缓存（localStorage 持久化）是只取了前 100 条、没有 isActive 的形状，换 key 让它自然失效。
// Owner 没有单一 name 字段，展示用 `firstName + ' ' + lastName`；
// nameFields 传 'id firstName lastName isActive'，让后端只返回这几个字段。
// 数据形态 [{id, name, isActive}]，name 由前端拼接以匹配 UseLookupResult 约定。
// 含停用业主（筛选历史数据要能选到）；新建 / 指派场景由调用方按 isActive === false 过滤掉。
export const OWNER_LOOKUP_MAX_RESULTS = 500;
export const fetchOwnerLookup = async (): Promise<Array<{ id: string; name: string; isActive: boolean }>> => {
  // 走 POST /pms/owners/lookup（不分页数组）。以前调的是 GET /pms/owners 列表接口：它回
  // { results, pagination } 而且只有第一页，`.map` 直接抛错，各业主门户页的 Owner 筛选下拉一直是空的
  // （2026-10-17 走查发现）。lookup 默认只回 100 条（后端 max_results 默认 100、上限 500），显式要 500。
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = (await ownersApi.lookupOwners({
    nameFields: 'id firstName lastName isActive',
    orderBy: 'firstName',
    includeInactive: true,
    maxResults: OWNER_LOOKUP_MAX_RESULTS,
  })) as any;
  const items = Array.isArray(res) ? res : Array.isArray(res?.results) ? res.results : [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return items.map((o: any) => ({
    id: o.id,
    name: [o.firstName, o.lastName].filter(Boolean).join(' '),
    // 后端没回这个字段（旧版本）时按启用算，不把人藏掉
    isActive: o.isActive !== false,
  }));
};

export const useOwnerLookup = (opts?: { enabled?: boolean }): UseLookupResult => {
  return useLookup('owners_v2', fetchOwnerLookup, { enabled: opts?.enabled });
};

// 用于getUserLookup (key: "users")
export const useUserLookup = (opts?: { enabled?: boolean }): UseLookupResult => {
  return useLookup('users', () => userApi.getUserLookup({nameFields: 'id fullName', orderBy: 'fullName'}), { enabled: opts?.enabled });
};

// 用于getUserLookup 带增强字段 (key: "users_enhanced") - 包含头像、职位、部门信息，仅活跃用户
export const useUserLookupEnhanced = (opts?: { enabled?: boolean }): UseLookupResult => {
  return useLookup('users_enhanced', () => userApi.getUserLookup({
    nameFields: 'id fullName avatarUrl positionTypeCode departmentId',
    orderBy: 'fullName',
    filters: {
      isActive: {
        type: 'boolean',
        operator: 'equals',
        value: true
      }
    }
  }), { enabled: opts?.enabled });
};

// 用于 task template 下拉选择 (key: "taskTemplates") — 仅活跃模板
// search() 返回 { results: [...] }，需要内联 map 成 array 后再存入缓存。
export const useTaskTemplateLookup = (opts?: { enabled?: boolean }): UseLookupResult => {
  return useLookup(
    'taskTemplates',
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = (await taskTemplatesApi.search({
        page: 1, pageSize: 200, columns: 'id name taskTypeCode',
        filters: { isActive: { type: 'boolean', operator: 'equals', value: true } },
      })) as any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (res?.results ?? []).map((t: any) => ({
        id: t.id,
        name: t.name,
        taskTypeCode: t.taskTypeCode,
      }));
    },
    { enabled: opts?.enabled },
  );
};

// 缓存管理hooks
export const useLookupActions = () => {
  const dispatch = useAppDispatch();

  return {
    // 清除特定key的缓存
    clearCache: useCallback((key: string) => {
      dispatch(clearCache(key));
      pendingRequests.delete(key);
    }, [dispatch]),
    
    // 清除所有缓存
    clearAllCache: useCallback(() => {
      dispatch(clearAllCache());
      pendingRequests.clear();
    }, [dispatch]),
    
    // 清除过期缓存
    clearExpiredCache: useCallback(() => {
      dispatch(clearExpiredCache());
    }, [dispatch]),
    
    // 手动刷新数据（清除缓存并重新获取）
    refreshData: useCallback((key: string, apiCall: () => Promise<any>) => {
      dispatch(clearCache(key));
      pendingRequests.delete(key);
      // 重新获取数据
      void dispatch(fetchLookupData({ key, apiCall }));
    }, [dispatch])
  };
};

// 获取缓存状态的hook
export const useLookupCache = () => {
  return useAppSelector(state => state.lookup.cache);
};
